import { useState, useEffect, useMemo } from "react";

// react-router components
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import Dashboard from "layouts/dashboard";
import Billing from "layouts/billing";
import Notifications from "layouts/notifications";
import Profile from "layouts/profile";
import CreatePost from "layouts/createpost";
import SignIn from "layouts/authentication/sign-in";
import JoinUs from "layouts/authentication/join-us";
import SignUpPersonal from "layouts/authentication/sign-up-personal";
import SignUpBusiness from "layouts/authentication/sign-up-business";
import ProtectedRoutes from "utils/protectedRoutes";
import Settings from "layouts/settings";
import EditProfile from "layouts/editProfile";
import ProfilePostDetails from "layouts/profilePostDetails";
import Search from "layouts/search";
import SeeAll from "layouts/SeeAll";

import { useMediaQuery } from "react-responsive";

import HouseRoundedIcon from "@mui/icons-material/HouseRounded";
import AddBoxIcon from "@mui/icons-material/AddBox";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { BottomNavigation, BottomNavigationAction } from "@mui/material";
import Campaigns from "layouts/campaigns";
// import Reward from "layouts/rewards/Reward";
// import ClaimReward from "layouts/rewards/ClaimReward";
// import RewardWidget from "layouts/rewards/RewardWidget";
// import RewardHistory from "layouts/rewards/RewardHistory";
// import LikeDetails from "layouts/rewards/GridDetail/LikeDetails";
// import CommentDetail from "layouts/rewards/GridDetail/CommentDetail";
// import PostDetails from "layouts/rewards/GridDetail/PostDetail";
// import NftDetails from "layouts/rewards/GridDetail/NftDetail";
// import EventDetails from "layouts/rewards/GridDetail/EventDetail";
// import EngagementDetails from "layouts/rewards/GridDetail/Engagement";
// import OTP from "layouts/rewards/OTP";
import Verify from "layouts/authentication/verify";
import ForgotPassword from "layouts/authentication/forgot-password";
import ResetPassword from "layouts/authentication/reset-password";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import colors from "assets/theme-dark/base/colors";
import Explore from "layouts/tables";
import AuctionDetails from "layouts/tables/data/AuctionDetail";
// import { Info } from "layouts/rewards/Info";
import { GoogleOAuthProvider } from "@react-oauth/google";
// import OtpVerified from "layouts/rewards/OtpVerified";
import SetupProfile from "layouts/authentication/setup-profile";
import nftSvg from "assets/images/NFT.svg";
import { Reels } from "layouts/Reels";
import PublicPostDetails from "layouts/postDetails";
import Chats from "layouts/chats";
import Testing from "layouts/Testing";
import NfcIcon from "@mui/icons-material/Nfc";
import { useDispatch, useSelector } from "react-redux";
import { connectWallet } from "Store/slices/walletSlice";
import { getUserInfo } from "services/apiService";
import { getNotificationsCountData } from "services/apiService";
import { getListCampaigns } from "services/apiService";
import { getUserAppInfo } from "Store/slices/userInfoSlice";
import { getUserAppNotificationCount } from "Store/slices/Sugg&NotificationSlice";
import { campaignReducer } from "Store/slices/campaignSlice";
import { fetchCurrentUserInfo } from "Store/slices/userInfoSlice";

import { fetchReward } from "Store/slices/rewardSlice";
import { fetchSuggestions } from "Store/slices/Sugg&NotificationSlice";
import Rewards from "layouts/reward";
import ClaimRewards from "layouts/reward/claimReward";
import OTP from "layouts/reward/OTP";
import OtpVerified from "layouts/reward/OtpVerified";
import RewardDetails from "layouts/reward/components/RewardDetails";
import EarnSingh from "layouts/reward/earnSingh";
import LoginDialog from "layouts/dialogModals/loginDialog";
import { loginDialogReducer } from "Store/slices/loginDialogSlice";
import { validateToken } from "Store/slices/userAuthSlice";
// import RewardNew from "layouts/reward";
// import ClaimRewardNew from "layouts/reward/claimRewardNew";

const networks = {
  //testnet
  bscTestnet: {
    chainId: `0x${Number(97).toString(16)}`,
    chainName: "Binance Smart Chain Testnet",
    nativeCurrency: {
      name: "Binance Chain Native Token",
      symbol: "tBNB",
      decimals: 18,
    },
    rpcUrls: ["https://data-seed-prebsc-1-s1.binance.org:8545/"],
    blockExplorerUrls: ["https://testnet.bscscan.com/"],
  },
};

// const networks = {
//   //mainnet
//   bscMainnet: {
//     chainId: `0x${Number(56).toString(16)}`,
//     chainName: "Binance Smart Chain Mainnet",
//     nativeCurrency: {
//       name: "Binance Chain Native Token",
//       symbol: "BNB",
//       decimals: 18,
//     },
//     rpcUrls: ["https://bsc-dataseed.binance.org/"],
//     blockExplorerUrls: ["https://bscscan.com"],
//   },
// };

export default function App() {
  let navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();

  const { pathname } = useLocation();

  const { openBoolean } = useSelector((state) => state.loginDialogState);

  const { userAuthInfo, loginStatus, isLoadingAuth } = useSelector(
    (state) => state.userAuth
  );

  const { campaignList, campaignLoading, campaignPageNumber } = useSelector(
    (state) => state.campaigns
  );

  const { userAppInfo, isLoadingInfo } = useSelector((state) => state.userInfo);

  const { userAppNotificationCount } = useSelector(
    (state) => state.userSuggestionAndNotifications
  );

  const { isWeb3Enabled, address, chainId, balance } = useSelector(
    (state) => state.walletData
  );

  const ethereum = window.ethereum;
  const [error, setError] = useState("");
  const { singhGrey, singh, black } = colors;
  const [controller] = useMaterialUIController();
  const { layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode } =
    controller;

  const [createPostBoolean, setCreatePostBoolean] = useState(false);

  const [value, setValue] = useState("home");
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const isDesktopOrLaptop = useMediaQuery({
    query: "(min-width: 1224px)",
  });
  const isBigScreen = useMediaQuery({ query: "(min-width: 1824px)" });
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1224px)" });

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (searchParams.get("ut") && searchParams.get("ut") !== "") {
      // console.log("ut", searchParams.get("ut"));
      dispatch(
        validateToken({
          token: searchParams.get("ut"),
          name: searchParams.get("name"),
        })
      );
    } else {
      console.log("ut", "false");
    }

    if (loginStatus) {
      if (!userAppInfo) {
        fetchNotificationCountData();
        fetchUserInfo();
        fetchRewardInfo();
        fetchSuggestionData();
      }
    }
    fetchCampaignList();
  }, [loginStatus]);

  useEffect(() => {
    autoConnectWallet();
  }, [ethereum]);

  useEffect(() => {
    if (ethereum) {
      ethereum.on("accountsChanged", autoConnectWallet);
      ethereum.on("chainChanged", () => {
        window.location.reload();
      });
      return () => {
        ethereum?.removeListener("accountsChanged", autoConnectWallet);
      };
    }
  });

  const autoConnectWallet = (accounts) => {
    if (localStorage.getItem("@Mealswap/walletProviderConnected")) {
      // console.log("account", accounts);
      // console.log("yes1");
      dispatch(connectWallet("ethereum"));
    }
  };

  const fetchUserInfo = async () => {
    // const userInfoRes = await getUserInfo(localStorage.userNameSingh);
    // // console.log("userINfo", userInfoRes);

    // if (userInfoRes.status === 200) {
    //   dispatch(getUserAppInfo(userInfoRes.data));
    // }

    dispatch(fetchCurrentUserInfo(localStorage.userNameSingh));
  };

  const fetchSuggestionData = async () => {
    // const userInfoRes = await getUserInfo(localStorage.userNameSingh);
    // // console.log("userINfo", userInfoRes);

    // if (userInfoRes.status === 200) {
    //   dispatch(getUserAppInfo(userInfoRes.data));
    // }

    dispatch(fetchSuggestions(localStorage.userNameSingh));
  };

  const fetchRewardInfo = async () => {
    dispatch(fetchReward());
  };

  const fetchNotificationCountData = async () => {
    const getNotificationCountDataRes = await getNotificationsCountData();
    // console.log("getNotificationCountDataRes", getNotificationCountDataRes);
    if (getNotificationCountDataRes.status === 200) {
      // setNotificationCount(getNotificationCountDataRes.data.Notification);
      dispatch(
        getUserAppNotificationCount(
          getNotificationCountDataRes.data.Notification
        )
      );
    }
  };

  const fetchCampaignList = async () => {
    if (campaignLoading) {
      const campaignListRes = await getListCampaigns(campaignPageNumber);
      dispatch(campaignReducer(campaignListRes));
    }
  };

  // useEffect(() => {
  //   firebaseNotification();
  // }, []);

  // const firebaseNotification = () => {
  //   onMessage(messaging, (message) => {
  //     console.log("Message", message);

  //     let bodyContent;
  //     if (message.notification.body.includes("\n")) {
  //       const bodyLines = message.notification.body.split("\n");
  //       bodyContent = (
  //         <div>
  //           {bodyLines[0]}
  //           <br />
  //           {bodyLines[1]}
  //         </div>
  //       );
  //     } else {
  //       bodyContent = <div>{message.notification.body}</div>;
  //     }

  //     toast.success(
  //       // `${message.notification.title}\n${message.notification.body}`,
  //       <div>
  //         {message.notification.title}
  //         <br />
  //         {bodyContent}
  //       </div>,
  //       {
  //         style: {
  //           fontSize: "12px",
  //         },
  //       }
  //     );
  //   });
  // };

  // Open sidenav when mouse enter on mini sidenav
  // const handleOnMouseEnter = () => {
  //   if (miniSidenav && !onMouseEnter) {
  //     setMiniSidenav(dispatch, false);
  //     setOnMouseEnter(true);
  //   }
  // };

  // Close sidenav when mouse leave mini sidenav
  // const handleOnMouseLeave = () => {
  //   if (onMouseEnter) {
  //     setMiniSidenav(dispatch, true);
  //     setOnMouseEnter(false);
  //   }
  // };

  // Change the openConfigurator state
  // const handleConfiguratorOpen = () =>
  //   setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  // useEffect(() => {
  //   document.body.setAttribute("dir", direction);
  // }, [direction]);

  // Setting page scroll to 0 when changing the route
  // useEffect(() => {
  //   document.documentElement.scrollTop = 0;
  //   document.scrollingElement.scrollTop = 0;
  // }, [pathname]);

  // const getRoutes = (allRoutes) =>
  //   allRoutes.map((route) => {
  //     if (route.collapse) {
  //       return getRoutes(route.collapse);
  //     }

  //     if (route.route) {
  //       return (
  //         <Route
  //           exact
  //           path={route.route}
  //           element={route.component}
  //           key={route.key}
  //         />
  //       );
  //     }

  //     return null;
  //   });

  // const configsButton = (
  //   <MDBox
  //     display="flex"
  //     justifyContent="center"
  //     alignItems="center"
  //     width="3.25rem"
  //     height="3.25rem"
  //     bgColor="white"
  //     shadow="sm"
  //     borderRadius="50%"
  //     position="fixed"
  //     right="2rem"
  //     bottom="2rem"
  //     zIndex={99}
  //     color="dark"
  //     sx={{ cursor: "pointer" }}
  //     onClick={handleConfiguratorOpen}
  //   >
  //     <Icon fontSize="small" color="inherit">
  //       settings
  //     </Icon>
  //   </MDBox>
  // );

  const handlePopupOpen = () => {
    if (!loginStatus) {
      dispatch(loginDialogReducer(true));
      return;
    }

    setCreatePostBoolean(true);
  };

  const handleClose = () => {
    setCreatePostBoolean(false);
  };

  const handleMobileNav = (path) => {
    if (!loginStatus) {
      dispatch(loginDialogReducer(true));
      return;
    }
    navigate(`${path}`);
  };

  const handleCloseOpenDialog = () => {
    dispatch(loginDialogReducer(false));
  };

  const mobileButtons = (
    <BottomNavigation
      sx={{
        backgroundColor: `${black.main}`,
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,

        "& .MuiBottomNavigationAction-root": {
          color: `${singhGrey.main}`,
        },

        "& .MuiBottomNavigationAction-root.Mui-selected": {
          color: `${singh.main}`,
        },
      }}
      showLabels={true}
      // elevation={3}
      value={value}
      onChange={handleChange}
    >
      <BottomNavigationAction
        onClick={() => handleMobileNav("/")}
        label="Home"
        value="home"
        icon={<HouseRoundedIcon />}
      />
      <BottomNavigationAction
        onClick={() => handlePopupOpen()}
        label="Create"
        value="create"
        icon={<AddBoxIcon />}
      />
      <BottomNavigationAction
        onClick={() => handleMobileNav("/nft-marketplace")}
        label="NFT"
        value="nft-marketplace"
        icon={<NfcIcon />}
      />
      <BottomNavigationAction
        onClick={() =>
          handleMobileNav(`/profile/?username=${localStorage.userNameSingh}`)
        }
        label="Profile"
        value="profile"
        icon={<AccountCircleIcon />}
      />
    </BottomNavigation>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <GoogleOAuthProvider clientId="402757163916-1upt7ms49vggd6kcr976krsarcgo5cqh.apps.googleusercontent.com">
        {/* <WalletProvider> */}
        <div
          style={{
            // display: isDesktopOrLaptop ? "flex" : "",
            display: "flex",
            justifyContent: "center",
          }}
        >
          {layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={
                  (transparentSidenav && !darkMode) || whiteSidenav
                    ? brandDark
                    : brandWhite
                }
                brandName="Material Dashboard 2"
                routes={[]}
                // onMouseEnter={handleOnMouseEnter}
                // onMouseLeave={handleOnMouseLeave}
              />

              {createPostBoolean && (
                <CreatePost
                  openStatus={createPostBoolean}
                  onClose={() => handleClose()}
                />
              )}

              {isTabletOrMobile && mobileButtons}
            </>
          )}
          <Routes>
            {/* {getRoutes(routes)} */}
            <Route element={<ProtectedRoutes />}>
              {/* <Route path="/" element={<Dashboard />} /> */}
              <Route path="/setup-profile" element={<SetupProfile />} />
              <Route path="/notifications" element={<Notifications />} />
              {/* <Route path="/chats" element={<Chats />} /> */}
              <Route path="/edit-profile" element={<EditProfile />} />
              <Route path="/post-details" element={<PublicPostDetails />} />
              <Route path="/rewards" element={<Rewards />} />
              <Route path="/create-post" element={<CreatePost />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/claim" element={<ClaimRewards />} />
              <Route path="/otp" element={<OTP />} />
              <Route path="/verified" element={<OtpVerified />} />
              <Route path="/reward-details" element={<RewardDetails />} />
              <Route path="/earn-singh" element={<EarnSingh />} />
            </Route>
            <Route path="/" element={<Dashboard />} />
            <Route
              path="/profile-post-detail"
              element={<ProfilePostDetails />}
            />
            <Route path="/profile" element={<Profile />} />
            <Route path="/reels" element={<Reels />} />
            <Route path="/auction-details" element={<AuctionDetails />} />
            <Route path="/see-all" element={<SeeAll />} />
            <Route path="/search" element={<Search />} />
            <Route path="/campaigns" element={<Campaigns />} />
            <Route path="/nft-marketplace" element={<Explore />} />

            {/* <Route path="/reward-history" element={<RewardHistory />} />
              <Route path="/reward-widget" element={<RewardWidget />} />

              <Route path="/like" element={<LikeDetails />} />
              <Route path="/comment" element={<CommentDetail />} />
              <Route path="/post" element={<PostDetails />} />
              <Route path="/nft" element={<NftDetails />} />
              <Route path="/event" element={<EventDetails />} />
              <Route path="/engagement" element={<EngagementDetails />} />

              <Route path="info" element={<Info />} />
              <Route path="earned" element={<RewardWidget />} />
         
               */}

            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/join-us" element={<JoinUs />} />
            <Route path="/sign-up-personal" element={<SignUpPersonal />} />
            <Route path="/sign-up-business" element={<SignUpBusiness />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/testing" element={<Testing />} />
          </Routes>

          {openBoolean && (
            <LoginDialog
              openStatus={openBoolean}
              onClose={() => handleCloseOpenDialog()}
            />
          )}
        </div>
        {/* </WalletProvider> */}
      </GoogleOAuthProvider>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        // draggable
        pauseOnHover
        theme="colored"
      />
    </ThemeProvider>
  );
}
